import * as React from 'react';
import { Flex, FlexDirection } from 'src/components/Flex/Flex';
import styled from 'styled-components';
import ReactTable, { Column } from 'react-table';
import 'src/ReactTableStyles.css';
import 'react-table/react-table.css';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SolutionFile } from 'src/models/SolutionFile';
import { PreviewEvent, DownloadEvent } from 'src/components/GoogleAnalytics/GoogleAnalytics';
import * as moment from 'moment';
import {
  FileTypeColors,
  ErrorMessages,
  Color,
  FileTypes,
  NonPreviewableFileTypes,
  ImageFileTypes
} from '../Globals/Globals';
import { FilePreview } from 'src/components/FilePreview/FilePreview';

const FileTableWrapper = styled(Flex)`
  width: 100%;
`;

const StyledReactTable = styled(ReactTable)`
  width: 100%;
`;

const FileTypeText = styled.div`
  color: ${props => props.color};
  font-weight: 600;
`;

const TableButtonWrapper = styled.div`
  cursor: pointer;

  &:hover {
    color: ${Color.blue};
  }
`;

interface IFileTableState {
  showModal: boolean;
  isModalLoading: boolean;
  isFileDownloading: boolean;
  selectedFile?: SolutionFile;
  fileUrl?: string;
}

interface IFileTableProps {
  files?: SolutionFile[];
  downloadFile(id: string, fileType: string, fileTitle?: string): Promise<string>;
  previewFile(id: string, fileType: string): Promise<string>;
}

export class FileTable extends React.Component<IFileTableProps, IFileTableState> {
  constructor(props: IFileTableProps) {
    super(props);

    this.state = {
      showModal: false,
      isModalLoading: false,
      isFileDownloading: false
    };
  }

  public renderColumnWithLink(data: any) {
    return <Link to={'/offering/' + data.original.id}>{data.value}</Link>;
  }

  public renderTableHeader(text: string) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {text}
        <FontAwesomeIcon icon="sort" />
      </div>
    );
  }

  public onModalClose = () => {
    this.setState({ showModal: false });
  };

  public showPreview = (file: SolutionFile) => {
    this.setState({ isModalLoading: true, showModal: true, selectedFile: file }, () => {
      // If the image is an image, we don't have to generate the pdf preview through API.
      if (ImageFileTypes.indexOf(file.fileType) !== -1 || file.fileType === FileTypes.PDF) {
        return this.props.downloadFile(file.id, file.fileType).then(fileUrl => {
          PreviewEvent(file.title);
          this.setState({
            showModal: true,
            isModalLoading: false,
            fileUrl
          });
        });
      } else {
        return this.props.previewFile(file.contentDocumentId, file.fileType).then(fileUrl => {
          PreviewEvent(file.title);
          this.setState({
            showModal: true,
            isModalLoading: false,
            fileUrl
          });
        });
      }
    });
  };

  public startDownload = (file: SolutionFile) => {
    this.setState({ isFileDownloading: true });
    this.props.downloadFile(file.id, file.fileExtension, file.title).then(url => {
      DownloadEvent(file.title);
      this.setState({ isFileDownloading: false });
      this.download(url, file.title + '-' + file.id, file.fileExtension);
    });
  };

  public openExternalLink = (url: string) => {
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
    }
  };

  public download = (fileUrl: string, fileName: string, fileExtension: string) => {
    const element = document.createElement('a');
    element.setAttribute('href', fileUrl);
    element.setAttribute('download', `${fileName}.${fileExtension}`);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  };

  public render() {
    const columns: Column[] = [
      {
        Header: (data, column) => this.renderTableHeader('Material name'),
        accessor: 'title',
        Cell: row => <div>{row.value}</div>,
        minWidth: 200
      },
      {
        Header: (data, column) => this.renderTableHeader('Classification'),
        accessor: 'classification',
        Cell: row => <div>{row.value}</div>,
        minWidth: 150
      },
      {
        Header: (data, column) => this.renderTableHeader('File type'),
        accessor: 'fileType',
        Cell: row => <FileTypeText color={FileTypeColors[row.value]}>{row.value}</FileTypeText>,
        width: 150,
        resizable: false
      },
      {
        Header: (data, column) => this.renderTableHeader('Last modified'),
        accessor: 'lastModifiedDate',
        Cell: row => <div>{moment(row.value).format('DD.MM.YYYY')}</div>,
        width: 150,
        resizable: false
      },
      {
        Cell: row =>
          NonPreviewableFileTypes.indexOf(row.row._original.fileType) === -1 && (
            <TableButtonWrapper
              title="Preview"
              data-tip="Show preview"
              onClick={() => this.showPreview(row.row._original)}
            >
              <FontAwesomeIcon icon="eye" />
            </TableButtonWrapper>
          ),
        width: 43,
        resizable: false,
        sortable: false
      },
      {
        Cell: row => {
          const material = row.row._original;
          switch (material.fileType) {
            case FileTypes.LINK:
              return (
                <TableButtonWrapper
                  title="Open external link"
                  onClick={() => this.openExternalLink(material.contentUrl)}
                  data-tip="Open external link"
                >
                  <FontAwesomeIcon icon="external-link-square-alt" />
                </TableButtonWrapper>
              );
            default:
              return (
                <TableButtonWrapper
                  onClick={() => !this.state.isFileDownloading && this.startDownload(material)}
                  title="Download"
                  data-tip="Download file"
                >
                  {this.state.isFileDownloading ? (
                    <FontAwesomeIcon icon="circle-notch" className="fa-spin" />
                  ) : (
                    <FontAwesomeIcon icon="download" />
                  )}
                </TableButtonWrapper>
              );
          }
        },
        width: 43,
        resizable: false,
        sortable: false
      }
    ];

    return (
      <FileTableWrapper direction={FlexDirection.column}>
        <StyledReactTable
          data={this.props.files}
          columns={columns}
          minRows={4}
          pageSize={this.props.files && this.props.files.length}
          className="-striped -highlight"
          noDataText={ErrorMessages.noMaterials}
          showPageSizeOptions={false}
          showPageJump={false}
          showPagination={false}
        />
        {this.state.selectedFile && (
          <FilePreview
            show={this.state.showModal}
            onClose={this.onModalClose}
            file={this.state.selectedFile}
            fileUrl={this.state.fileUrl ? this.state.fileUrl : ''}
            loading={this.state.isModalLoading}
            download={this.startDownload}
          />
        )}
      </FileTableWrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/FileTable/FileTable.tsx